<template>
  <div>
    <!-- Acceptance Parsing -->
    <div class="form-group m-b-15">
      <label class="control-label col-md-3 col-sm-3">
        {{ $t('PING_TREE_TARGET_ACCEPTANCE_PARSING') }}
        <i
          v-tooltip.right="$t('PING_TREE_TARGET_ACCEPTANCE_PARSING_TT')"
          class="fa fa-question-circle m-l-5"
        />
      </label>
      <div
        v-for="(step, index) in acceptanceParsing"
        :key="index"
        class="col-md-9"
        :class="{'col-sm-offset-3': index > 0}"
      >
        <div class="display-flex flex--align-center m-b-10">
          <SearchableDropdown
            v-model="step.property"
            class="m-r-15 m-b-10 input--size-md"
            :config="selectablePropertiesConfig"
            :clearable="false"
            rules="required"
            required
            @change="updateAcceptanceParsingSettings"
          />
          <SearchableDropdown
            v-model="step.operator"
            class="m-r-15 m-b-10 input--size-sm"
            :config="acceptanceParsingComparisonConfig"
            :clearable="false"
            rules="required"
            required
            @change="updateAcceptanceParsingSettings"
          />
          <ValidationProvider
            v-slot="{ classes }"
            :ref="`acceptanceParsingValue${index}`"
            rules="required"
            slim
          >
            <div :class="classes">
              <input
                v-model="step.value"
                :name="`acceptanceParsingValue${index}`"
                type="text"
                class="form-control m-r-15 m-b-10 width-auto"
                @change="updateAcceptanceParsingSettings"
              />
              <label 
                v-t="'REQUIRED'"
                class="form-group__label-required"
              />
            </div>
          </ValidationProvider>
          <div :class="index === 0 ? 'action-icon text-right p-l-15 invisible' : 'action-icon text-right p-l-15'">
            <a class="btn btn-list-function" :title="$t('REMOVE_PARSING_STEP')"  @click="removeAcceptanceParsingStep(index)">
                <i class="fa fa-trash f-s-16"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
    <!-- Dynamic Bid Parsing -->
     <div class="form-group m-b-15" v-if="isDynamicRevenueEnabled">
      <label class="control-label col-md-3 col-sm-3">
        {{ $t('PING_TREE_TARGET_DYNAMIC_BID_PARSING') }}
        <i
          v-tooltip.right="$t('PING_TREE_TARGET_DYNAMIC_BID_PARSING_TT')"
          class="fa fa-question-circle m-l-5"
        />
      </label>
        <div class="display-flex m-b-10 col-md-9">
          <SearchableDropdown
            v-model="dynamicBidParsing[0].property"
            class="m-r-15 m-b-10 input--size-md"
            :config="selectablePropertiesConfig"
            :clearable="false"
            rules="required"
            required
            @change="updateDynamicBidParsingSettings"
          />
          <label 
                v-t="'REQUIRED'"
                class="form-group__label-required"
              />
        </div>
    </div>
     <!-- Dynamic Number Parsing -->
     <div class="form-group m-b-15" v-if="isDynamicNumberEnabled">
      <label class="control-label col-md-3 col-sm-3">
        {{ $t('PING_TREE_TARGET_DYNAMIC_NUMBER_PARSING') }}
        <i
          v-tooltip.right="$t('PING_TREE_TARGET_DYNAMIC_NUMBER_PARSING_TT')"
          class="fa fa-question-circle m-l-5"
        />
      </label>
        <div class="display-flex m-b-10 col-md-9">
          <SearchableDropdown
            v-model="dynamicNumberParsing[0].property"
            class="m-r-15 m-b-10 input--size-md"
            :config="selectablePropertiesConfig"
            :clearable="false"
            rules="required"
            required
            @change="updateDynamicNumberParsingSettings"
          />
          <label 
                v-t="'REQUIRED'"
                class="form-group__label-required"
              />
        </div>
    </div>
     <!-- Dynamic Duration Parsing -->
     <div class="form-group m-b-15" v-if="isDynamicDurationEnabled">
      <label class="control-label col-md-3 col-sm-3">
        {{ $t('PING_TREE_TARGET_DYNAMIC_DURATION_PARSING') }}
        <i
          v-tooltip.right="$t('PING_TREE_TARGET_DYNAMIC_DURATION_PARSING_TT')"
          class="fa fa-question-circle m-l-5"
        />
      </label>
        <div class="display-flex m-b-10 col-md-9">
          <SearchableDropdown
            v-model="dynamicDurationParsing[0].property"
            class="m-r-15 m-b-10 input--size-md"
            :config="selectablePropertiesConfig"
            :clearable="false"
            rules="required"
            required
            @change="updateDynamicDurationParsingSettings"
          />
          <label 
                v-t="'REQUIRED'"
                class="form-group__label-required"
              /> 
        </div>
    </div>
  </div> 
</template>

<script>
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { acceptanceParsingComparisonConfig } from '@/modules/dynamicInboundCalls/helpers/constants';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';

export default {
  name: 'RingTreeTargetParsingSettings',
  components: {
    SearchableDropdown
  }, 
  props: {
    acceptanceParsing: {
      type: Array,
      default() {
        return [];
      },
    },
    isDynamicNumberEnabled: {
      type: Boolean,
      default: false
    },
    isDynamicRevenueEnabled: {
      type: Boolean,
      default: false
    },
    isDynamicDurationEnabled: {
      type: Boolean,
      default: false
    },
    dynamicBidParsing: {
      type: Array,
      default() {
        return [];
      },
    },
    dynamicNumberParsing: {
      type: Array,
      default() {
        return [];
      },
    },
    dynamicDurationParsing: {
      type: Array,
      default() {
        return [];
      },
    },
    selectablePropertiesConfig: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  created() {
    this.acceptanceParsingComparisonConfig = acceptanceParsingComparisonConfig;
  },
  methods: {
    updateAcceptanceParsingSettings() {
      this.$emit('updateAcceptanceParsingSettings');
    },
    updateDynamicNumberParsingSettings() {
      this.$emit('updateDynamicNumberParsingSettings');
    },
    updateDynamicBidParsingSettings() {
      this.$emit('updateDynamicBidParsingSettings');
    },
    updateDynamicDurationParsingSettings() {
      this.$emit('updateDynamicDurationParsingSettings');
    },
    addDynamicBidParsingStep() {
      this.$emit('addDynamicBidParsingStep');
    },
    addDynamicDurationParsingStep() {
      this.$emit('addDynamicDurationParsingStep');
    },
    addDynamicNumberParsingStep() {
      this.$emit('addDynamicNumberParsingStep');
    },
    removeAcceptanceParsingStep(index) {
      this.$emit('removeAcceptanceParsingStep', index);
    },
    removeDynamicBidParsingStep(index) {
      this.$emit('removeDynamicBidParsingStep', index);
    },
    removeDynamicDurationParsingStep(index) {
      this.$emit('removeDynamicDurationParsingStep', index);
    },
    removeDynamicNumberParsingStep(index) {
      this.$emit('removeDynamicNumberParsingStep', index);
    },
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  } 
};
</script>